import React, { PropsWithChildren, useCallback, useState } from 'react';
import { DevicePhoneMobileIcon, MapPinIcon } from '@heroicons/react/24/outline';

import { cn, FireIcon, useResponsiveBreakPoints } from '@restworld/utils-common';
import { Avatar, Badge, Button, type ButtonVariantType, IBadgeProps, IconButton, Heading } from '@restworld/ui-ds-base';

import { WhatsappIcon } from '@restworld/utils-common';

export interface EmployerCandidateCardProps {
  ctaButton: { label: string; onClick?: () => void; variant?: ButtonVariantType };
  className?: string;
  newShortlistedSection?: boolean;
}

export function EmployerCandidateCard({
  children,
  ctaButton,
  className,
  newShortlistedSection,
}: PropsWithChildren<EmployerCandidateCardProps>) {
  const [isHovered, setIsHovered] = useState(false);
  const { isDesktop } = useResponsiveBreakPoints();
  return (
    <CardContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={ctaButton?.onClick}
      className={className}
    >
      {children}
      {(isDesktop || newShortlistedSection) && (
        <CardFooterWrapper isHovered={newShortlistedSection ? true : isHovered}>
          <Button fullwidth size="sm" variant={ctaButton?.variant}>
            {ctaButton?.label}
          </Button>
        </CardFooterWrapper>
      )}
    </CardContainer>
  );
}

interface EmployerCandidateCardHeaderProps {
  firstName?: string;
  lastName?: string;
}
export function EmployerCandidateCardHeader({ firstName, lastName }: EmployerCandidateCardHeaderProps) {
  const truncateText = (text?: string, maxLength = 35) => {
    if (!text) return text;
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };
  return (
    <div className="flex gap-1">
      <Heading as="span" size={6} weight="semibold" className="leading-normal text-sm text-neutral-700">
        {truncateText(firstName)}
      </Heading>
      <Heading as="span" size={6} weight="semibold" className="leading-normal text-sm text-neutral-700">
        {truncateText(lastName)}
      </Heading>
    </div>
  );
}

export function EmployerCandidateCardInfo({
  candidateImage,
  firstName,
  lastName,
  badge,
  distance,
  showFire,
}: {
  candidateImage?: string;
  firstName?: string;
  lastName?: string;
  badge: { variant: IBadgeProps['variant']; text: string };
  distance?: string;
  showFire?: boolean;
}) {
  return (
    <div className="flex gap-3 mb-1">
      <RowImageBadgeContainer>
        <Avatar src={candidateImage} firstName={firstName} lastName={lastName} size="md" />
        {showFire && (
          <span className="absolute -top-2 -right-2 text-base z-10 pointer-events-none">
            <FireIcon className="h-6 w-6 text-[#9E5400]" />
          </span>
        )}
      </RowImageBadgeContainer>
      <div>
        <div className="whitespace-nowrap min-w-fit max-w-fit">
          <Badge variant={badge.variant}>{`${badge.text} ${badge.variant === 'fresh' ? '🎉' : ''}`}</Badge>
        </div>
        <div className="mt-2 flex items-center">
          {distance && (
            <>
              <MapPinIcon className="h-4 w-4 stroke-neutral-200" />
              <p className="text-sm text-neutral-200 font-medium">{distance}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface EmployerCandidatesCardLabelsProps {
  labels: string[];
  variant: IBadgeProps['variant'];
}
export function EmployerCandidatesCardLabels({ labels, variant }: EmployerCandidatesCardLabelsProps) {
  return (
    <div className="flex flex-wrap gap-1">
      {labels.map((label, i) =>
        label ? (
          <div className="whitespace-nowrap min-w-fit max-w-fit" key={i}>
            <Badge variant={variant}>{label}</Badge>
          </div>
        ) : null
      )}
    </div>
  );
}

export default EmployerCandidateCard;

// New Row-direction variants for JP dashboard shortlisted section
export interface EmployerCandidateRowCardProps {
  ctaButton: { label: string; onClick?: () => void; variant?: ButtonVariantType };
  className?: string;
  /**
   * Optional content rendered as a NEW row at the bottom of the card, beneath
   * the main name/distance/labels/cta row. Rendered after the hover CTA so the
   * grid auto-flow keeps the CTA on the main row.
   */
  footerSlot?: React.ReactNode;
}

export function EmployerCandidateRowCard({
  children,
  ctaButton,
  className,
  footerSlot,
}: PropsWithChildren<EmployerCandidateRowCardProps>) {
  const [isHovered, setIsHovered] = useState(false);
  const { isDesktop } = useResponsiveBreakPoints();
  return (
    <RowCardContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={ctaButton?.onClick}
      className={className}
    >
      {children}
      {isDesktop && (
        <div
          className={cn(
            'absolute inset-y-0 right-0 flex items-center justify-end pl-8 pr-7 bg-neutral-10 rounded-r-2xl transition-opacity duration-200',
            isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <Button className="!h-12" size="sm" variant={ctaButton?.variant}>
            {ctaButton?.label}
          </Button>
        </div>
      )}
      {footerSlot}
    </RowCardContainer>
  );
}

export function EmployerCandidateRowInfo({
  firstName,
  lastName,
  badge,
  candidateImage,
  showFire,
}: {
  candidateImage?: string;
  showFire?: boolean;
  firstName?: string;
  lastName?: string;
  badge: { variant: IBadgeProps['variant']; text: string };
}) {
  const truncateText = (text?: string, maxLength = 15) => {
    if (!text) return text;
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };
  return (
    <div className="col-span-2 flex flex-row gap-2 min-w-[10rem]">
      <RowImageBadgeContainer>
        <Avatar src={candidateImage} firstName={firstName} lastName={lastName} size="md" />
        {showFire && (
          <span className="absolute -top-2 -right-2 text-base z-10 pointer-events-none">
            <FireIcon className="h-6 w-6 text-[#9E5400]" />
          </span>
        )}
      </RowImageBadgeContainer>
      <div className="flex flex-col">
        <Heading as="span" size={6} weight="semibold" className="leading-normal text-sm text-neutral-700">
          {truncateText(firstName)}
        </Heading>
        <Heading as="span" size={6} weight="semibold" className="leading-normal text-sm text-neutral-700">
          {truncateText(lastName)}
        </Heading>
        <Badge variant={badge.variant}>{`${badge.text} ${badge.variant === 'fresh' ? '🎉' : ''}`}</Badge>
      </div>
    </div>
  );
}

export function EmployerCandidateRowDistance({ distance }: { distance?: string }) {
  if (!distance) return null;
  return (
    <div className="flex items-center justify-center gap-1">
      <MapPinIcon className="h-4 w-4 stroke-neutral-200" />
      <p className="text-sm text-neutral-200 font-medium">{distance}</p>
    </div>
  );
}

// Shown on the "Non idonei" tab in place of the strength labels: a single
// concise sentence (generated by the evaluation) explaining why the candidate
// is not suitable for the position.
export function EmployerCandidateUnsuitabilityReason({ reason, row }: { reason: string; row?: boolean }) {
  return row ? (
    <RowUnsuitabilityReasonWrapper>
      <UnsuitabilityReasonText>{reason}</UnsuitabilityReasonText>
    </RowUnsuitabilityReasonWrapper>
  ) : (
    <UnsuitabilityReasonText>{reason}</UnsuitabilityReasonText>
  );
}

export function EmployerCandidateRowLabels({ labels }: { labels: string[] }) {
  const validLabels = (labels || []).filter(Boolean);
  const maxVisible = 5;
  const visibleLabels = validLabels.slice(0, maxVisible);
  const remainingCount = validLabels.length - visibleLabels.length;

  return (
    <div className="col-span-5 flex flex-wrap gap-2">
      {visibleLabels.map((label, i) => (
        <div className="whitespace-nowrap min-w-fit max-w-fit" key={i}>
          <Badge variant="neutral">{label}</Badge>
        </div>
      ))}
      {remainingCount > 0 && (
        <div className="whitespace-nowrap min-w-fit max-w-fit">
          <Badge variant="neutral">{`+${remainingCount}`}</Badge>
        </div>
      )}
    </div>
  );
}

export function EmployerCandidateRowContact({
  phoneNumber,
  whatsappNumber,
}: {
  phoneNumber?: string;
  whatsappNumber?: string;
}) {
  const onPhoneClick = useCallback(() => {
    window.location.href = `tel:${phoneNumber}`;
  }, [phoneNumber]);
  const onWhatsappClick = useCallback(() => {
    window.open(`https://wa.me/${whatsappNumber}`, '_blank', 'noopener,noreferrer');
  }, [whatsappNumber]);

  return (
    <div className="flex items-center justify-center gap-6">
      {phoneNumber && (
        <IconButton
          ariaLabel="call"
          Icon={DevicePhoneMobileIcon}
          onClick={onPhoneClick}
          className="grid place-items-center"
        />
      )}
      {whatsappNumber && (
        <IconButton
          ariaLabel="whatsapp"
          Icon={WhatsappIcon}
          onClick={onWhatsappClick}
          className="grid place-items-center"
        />
      )}
    </div>
  );
}

// Functional wrapper components replacing styled-components

const CardContainer = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'relative px-4 pb-4 pt-5 border border-neutral-40 rounded-2xl bg-neutral-10 flex flex-col gap-3 box-border transition-all hover:border-purple-500 hover:shadow-[inset_0_0_0_3px_theme(colors.purple.500)] w-full cursor-pointer',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

const CardFooterWrapper = ({
  isHovered,
  className,
  ...props
}: { isHovered?: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'transition-all duration-200 opacity-0 absolute bottom-2 left-[3px] right-[3px] h-15 z-[5] px-4 grid place-items-center bg-neutral-10 border-t border-t-neutral-40 rounded-b-2xl',
      isHovered && 'opacity-100 bottom-[3px]',
      className
    )}
    {...props}
  />
);

const RowCardContainer = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'relative px-5 py-4 border border-neutral-40 rounded-2xl bg-neutral-10 grid grid-cols-9 items-center justify-center box-border transition-all hover:border-purple-500 hover:shadow-[inset_0_0_0_3px_theme(colors.purple.500),0px_4px_16px_0px_rgba(0,0,26,0.15)] w-full cursor-pointer',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

const RowImageBadgeContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('relative inline-block', className)} {...props} />
);

const RowUnsuitabilityReasonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('col-span-5 flex items-center', className)} {...props} />
);

const UnsuitabilityReasonText = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-neutral-200 italic', className)} {...props} />
);
